import type { ID } from "../types";
import {
  buildCategoryKey,
  buildCategoryParentKey,
} from "../library/utilization/CategoryKeyUtilization";
import { constructCombinedBranchAssignmentReportMap } from "../library/utilization/ReportUtilization";

interface IActivityScoreDetail {
  activityId: ID;
  activityTemplateId?: ID;
  activityTemplateType?: string;
  score?: number | null;
  timeSpent?: number;
  categoryIdList?: ID[];
  empty?: boolean;
  /** When the answer counts for the Zaman aralığı filter — stamped from its item at ingest. */
  endAt?: number | null;
}

interface ICategoryRef {
  id: ID;
  code: string;
  name: string;
  versionCode?: string;
  parentCode?: string;
}

interface IActivityScoreItem {
  activityId: ID;
  score?: number | null;
  timeSpent?: number;
  endAt?: number | null;
}

interface ICategoryActivityAnswerNode {
  categoryDTO: ICategoryRef;
  activityScoreDTOSet: IActivityScoreItem[];
  subCategoryActivityAnswerDTOList: ICategoryActivityAnswerNode[];
  aggregatedActivityCount?: number;
  aggregatedScoreSum?: number;
  aggregatedAveragePercentage?: number | null;
  aggregatedHighestActivityPercentage?: number | null;
  aggregatedLowestActivityPercentage?: number | null;
}

interface IPerformanceItem {
  branchAssignmentId?: ID;
  name?: string;
  description?: string;
  coterieType?: string;
  curriculumType?: string;
  createdAt?: number;
  beginAt?: number;
  endAt?: number;
  isEvaluated?: boolean;
  learnerScore?: number | null;
  activityScoreDetailDTOList?: IActivityScoreDetail[];
}

/**
 * Builds categoryActivityAnswerDTOList tree from flat activity score details
 * and local category data, matching the structure expected by report screens.
 */
export interface IOutcomeTreeBuildOptions {
  /**
   * Keep blank / never-answered rows (`empty: true`) as score 0 instead of dropping them, so a node's %
   * is score ÷ ALL of its questions (the PO success-% rule, every kind). The learner's holistic trees
   * (şube, destek, bireysel, yazılı) pass it; it is opt-in only so the deneme trees — whose rows are
   * synthesised per question and never `empty` — and the deneme karne breakdown stay as they were.
   */
  countBlankAnswers?: boolean;
}

export const buildCategoryActivityAnswerDTOList = (
  activityScoreDetailDTOList: IActivityScoreDetail[] | null | undefined,
  categoryDTOList: ICategoryRef[] | null | undefined,
  options: IOutcomeTreeBuildOptions = {},
): ICategoryActivityAnswerNode[] => {
  const { countBlankAnswers = false } = options;
  if (!activityScoreDetailDTOList || activityScoreDetailDTOList.length === 0) {
    return [];
  }
  if (!categoryDTOList || categoryDTOList.length === 0) {
    return [];
  }

  // Build lookup maps. Keyed by versionCode + code, never by code alone: the MEB
  // catalogue reuses a code across curriculum generations (`SB.5.1.` is both the
  // 2018 BİREY VE TOPLUM and the 2024 BİRLİKTE YAŞAMAK) and a profile loads both,
  // so a code-only map merged two curricula's outcomes into one report node and
  // rolled 2024 scores up under retired 2018 theme names.
  const categoryByIdMap: Record<string, ICategoryRef> = {};
  const categoryByKeyMap: Record<string, ICategoryRef> = {};
  categoryDTOList.forEach((cat) => {
    categoryByIdMap[String(cat.id)] = cat;
    categoryByKeyMap[buildCategoryKey(cat)] = cat;
  });

  // Group activity scores by leaf category ID. By default skipped/empty answers are excluded
  // (engagement lives separately in completionStatus / completionPercentage on the item). With
  // `countBlankAnswers` an empty row is kept as score 0, so it stays in the node's denominator.
  const leafCategoryActivityMap: Record<string, IActivityScoreItem[]> = {};
  activityScoreDetailDTOList.forEach((activityScore) => {
    const { activityId, timeSpent, categoryIdList, empty, endAt } =
      activityScore;
    if (empty && !countBlankAnswers) return;
    const score = empty ? (activityScore.score ?? 0) : activityScore.score;
    if (score == null) return;
    if (!categoryIdList) return;
    categoryIdList.forEach((categoryId) => {
      const category = categoryByIdMap[String(categoryId)];
      if (!category) return;
      const key = String(categoryId);
      if (!leafCategoryActivityMap[key]) {
        leafCategoryActivityMap[key] = [];
      }
      leafCategoryActivityMap[key].push({
        activityId,
        score,
        timeSpent,
        // Carried only when the caller stamped it, so a row keeps its exact old shape otherwise.
        ...(endAt != null ? { endAt } : {}),
      });
    });
  });

  // Build tree nodes bottom-up
  const nodeMap: Record<string, ICategoryActivityAnswerNode> = {};

  const getOrCreateNode = (
    categoryDTO: ICategoryRef,
  ): ICategoryActivityAnswerNode => {
    const key = buildCategoryKey(categoryDTO);
    if (nodeMap[key]) return nodeMap[key];
    nodeMap[key] = {
      categoryDTO: {
        id: categoryDTO.id,
        name: categoryDTO.name,
        code: categoryDTO.code,
        versionCode: categoryDTO.versionCode,
        parentCode: categoryDTO.parentCode,
      },
      activityScoreDTOSet: [],
      subCategoryActivityAnswerDTOList: [],
    };
    return nodeMap[key];
  };

  // Create leaf nodes with activity scores
  Object.keys(leafCategoryActivityMap).forEach((categoryId) => {
    const category = categoryByIdMap[categoryId];
    if (!category) return;
    const node = getOrCreateNode(category);
    node.activityScoreDTOSet = leafCategoryActivityMap[categoryId];
  });

  // Walk up parentCode chains to build ancestors
  Object.keys(leafCategoryActivityMap).forEach((categoryId) => {
    const category = categoryByIdMap[categoryId];
    if (!category) return;

    // Walk the chain by category object, not by bare code — the parent must be
    // resolved inside the child's own versionCode or the walk crosses curricula.
    let currentCategory: ICategoryRef | undefined =
      categoryByKeyMap[buildCategoryKey(category)];
    // Every key this chain has already stood on. The walk advances by resolving a parentCode inside
    // the child's own versionCode, and nothing in the catalogue guarantees that chain is acyclic: a
    // row whose parentCode equals its own code resolves to ITSELF, so `currentCategory` never moves
    // and this loop spins forever — not a slow report, a hung browser tab, because it happens during
    // render. A row missing both code and versionCode does the same thing, since both keys collapse
    // to the empty key. Stop at the first repeat and keep the ancestors gathered so far.
    const visitedCategoryKeySet = new Set<string>();
    while (currentCategory) {
      const currentKey = buildCategoryKey(currentCategory);
      if (visitedCategoryKeySet.has(currentKey)) break;
      visitedCategoryKeySet.add(currentKey);
      const parentCategory =
        categoryByKeyMap[buildCategoryParentKey(currentCategory)];
      if (!parentCategory) break;
      if (visitedCategoryKeySet.has(buildCategoryKey(parentCategory))) break;

      const parentNode = getOrCreateNode(parentCategory);
      const childNode = nodeMap[buildCategoryKey(currentCategory)];

      if (
        childNode &&
        !parentNode.subCategoryActivityAnswerDTOList.find(
          (sub) =>
            buildCategoryKey(sub.categoryDTO) ===
            buildCategoryKey(childNode.categoryDTO),
        )
      ) {
        parentNode.subCategoryActivityAnswerDTOList.push(childNode);
      }
      currentCategory = parentCategory;
    }
  });

  // Find root nodes (those whose parentCode doesn't exist in our map)
  const rootNodes: ICategoryActivityAnswerNode[] = [];
  Object.values(nodeMap).forEach((node) => {
    const { parentCode } = node.categoryDTO;
    const parentKey = buildCategoryParentKey(node.categoryDTO);
    if (!parentCode || !categoryByKeyMap[parentKey] || !nodeMap[parentKey]) {
      if (
        !rootNodes.find(
          (r) =>
            buildCategoryKey(r.categoryDTO) ===
            buildCategoryKey(node.categoryDTO),
        )
      ) {
        rootNodes.push(node);
      }
    }
  });

  // Sort by code at each level
  const sortByCode = (
    nodes: ICategoryActivityAnswerNode[],
  ): ICategoryActivityAnswerNode[] => {
    nodes.sort((a, b) => a.categoryDTO.code.localeCompare(b.categoryDTO.code));
    nodes.forEach((node) => {
      if (node.subCategoryActivityAnswerDTOList.length > 0) {
        sortByCode(node.subCategoryActivityAnswerDTOList);
      }
    });
    return nodes;
  };

  const sortedRoots = sortByCode(rootNodes);
  sortedRoots.forEach(populateAggregatedFields);
  return sortedRoots;
};

/**
 * Merges the learner's per-assignment report details into ONE outcome tree and
 * leaves its aggregates consistent with the merged contents.
 *
 * `constructCombinedBranchAssignmentReportMap` only concatenates each node's
 * activityScoreDTOSet; it cannot fix the aggregates, because those were already
 * computed per assignment by `buildCategoryActivityAnswerDTOList` before the
 * merge and nothing recomputes them afterwards. Rendering the merged tree
 * therefore showed the FIRST assignment's numbers over all eleven assignments'
 * data — a root reading "15 soru" above leaves that already summed to more than
 * that, and a kazanım reading 0% where the learner actually averaged 22%.
 *
 * Two steps, in this order:
 *
 *  1. De-duplicate each leaf's score set by activityId. The same activity can
 *     belong to more than one assignment, and the concatenation keeps both
 *     copies. The backend counts an activity ONCE — its own learning-outcome
 *     report returns 23 for a node this merge leaves holding 27 rows — so
 *     summing without de-duplication would overshoot instead of undershoot.
 *  2. Re-run populateAggregatedFields over the merged tree. It assigns rather
 *     than accumulates, so running it a second time is safe and idempotent.
 *
 * Counts then match the backend node for node.
 */
export const buildMergedBranchAssignmentReportMap = (
  branchAssignmentReportDetailList: any[],
): Record<string, any[]> => {
  const map = constructCombinedBranchAssignmentReportMap(
    branchAssignmentReportDetailList || [],
  );
  Object.keys(map).forEach((coterieType) => {
    (map[coterieType] || []).forEach((root: any) => {
      deduplicateActivityScores(root);
      populateAggregatedFields(root);
    });
  });
  return map;
};

/** Drops repeated activityId rows from a node's score set, depth first. */
const deduplicateActivityScores = (node: any): void => {
  if (!node) return;
  (node.subCategoryActivityAnswerDTOList || []).forEach(
    deduplicateActivityScores,
  );
  const scoreSet = node.activityScoreDTOSet;
  if (!scoreSet) return;
  const list = Array.isArray(scoreSet) ? scoreSet : Array.from(scoreSet as any);
  const seen = new Set<any>();
  const unique: any[] = [];
  for (const entry of list) {
    // A row without an activityId cannot be matched against another, so it is
    // kept rather than silently collapsed into the first id-less row.
    if (entry?.activityId == null) {
      unique.push(entry);
      continue;
    }
    if (seen.has(entry.activityId)) continue;
    seen.add(entry.activityId);
    unique.push(entry);
  }
  node.activityScoreDTOSet = unique;
};

/**
 * Walks the tree bottom-up and populates aggregated score/count/percentage
 * fields on each node. Mirrors the backend formula in
 * BranchAssignmentReportUtilization.recursivelyCalculateAggregatedScores
 * so screens read pre-computed values instead of re-aggregating on render.
 */
const populateAggregatedFields = (node: ICategoryActivityAnswerNode): void => {
  const subList = node.subCategoryActivityAnswerDTOList || [];
  for (const sub of subList) {
    populateAggregatedFields(sub);
  }

  const scoreSet = node.activityScoreDTOSet || [];
  let count = scoreSet.length;
  let sum = 0;
  let highest: number | null = null;
  let lowest: number | null = null;
  for (const item of scoreSet) {
    if (item.score == null) continue;
    sum += item.score;
    const pct = item.score * 100;
    if (highest == null || pct > highest) highest = pct;
    if (lowest == null || pct < lowest) lowest = pct;
  }

  for (const sub of subList) {
    count += sub.aggregatedActivityCount ?? 0;
    sum += sub.aggregatedScoreSum ?? 0;
  }

  node.aggregatedActivityCount = count;
  node.aggregatedScoreSum = sum;
  node.aggregatedAveragePercentage = count > 0 ? (sum / count) * 100 : null;
  node.aggregatedHighestActivityPercentage = highest;
  node.aggregatedLowestActivityPercentage = lowest;
};

/**
 * The date an item's answers count on for the Zaman aralığı filter: its deadline when it has one (an
 * ödev), else its creation date (a yazılı; a deneme, whose createdAt is normalised to the sitting date
 * at ingest). The same date the gelişim grafiği plots the item on.
 */
const eventAtOf = (item: IPerformanceItem): number | null =>
  item.endAt ?? item.createdAt ?? null;

/**
 * Stamps every answer with its item's date. The outcome rows used to carry no date at all, so the
 * outcome views' time filter — which reads each row's `endAt` — dropped every row and a learner who
 * picked "Son 30 gün" saw an empty report.
 */
const stampScoreDetailsWithEventAt = (
  item: IPerformanceItem,
): IActivityScoreDetail[] => {
  const eventAt = eventAtOf(item);
  return (item.activityScoreDetailDTOList || []).map((detail) =>
    eventAt == null || detail.endAt != null ? detail : { ...detail, endAt: eventAt },
  );
};

/**
 * Aggregates a flat list of performance items (filtered to a single coterieType)
 * into a single outcome tree consumable by OutcomeGraph (and sibling views).
 */
export const buildOutcomeReportListFromPerformanceItems = (
  performanceItems: IPerformanceItem[] | null | undefined,
  categoryDTOList: ICategoryRef[],
  options: IOutcomeTreeBuildOptions = {},
): ICategoryActivityAnswerNode[] => {
  if (!performanceItems || performanceItems.length === 0) return [];
  const combinedScoreDetailList: IActivityScoreDetail[] = [];
  for (const item of performanceItems) {
    const scoreDetailList = stampScoreDetailsWithEventAt(item);
    for (const scoreDetail of scoreDetailList) {
      if (scoreDetail.empty && !options.countBlankAnswers) continue;
      combinedScoreDetailList.push(scoreDetail);
    }
  }
  return buildCategoryActivityAnswerDTOList(
    combinedScoreDetailList,
    categoryDTOList,
    options,
  );
};

/**
 * Groups performance items by coterieType then builds one outcome tree per coterie.
 * `options.countBlankAnswers` — see IOutcomeTreeBuildOptions.
 */
export const buildOutcomeReportMapByCoterie = (
  performanceItems: IPerformanceItem[] | null | undefined,
  categoryDTOList: ICategoryRef[],
  options: IOutcomeTreeBuildOptions = {},
): Record<string, ICategoryActivityAnswerNode[]> => {
  const byCoterie: Record<string, IPerformanceItem[]> = {};
  if (!performanceItems || performanceItems.length === 0) return {};
  for (const item of performanceItems) {
    if (item.isEvaluated === false) continue;
    const key = item.coterieType;
    if (!key) continue;
    if (!byCoterie[key]) byCoterie[key] = [];
    byCoterie[key].push(item);
  }
  const result: Record<string, ICategoryActivityAnswerNode[]> = {};
  for (const key of Object.keys(byCoterie)) {
    result[key] = buildOutcomeReportListFromPerformanceItems(
      byCoterie[key],
      categoryDTOList,
      options,
    );
  }
  return result;
};

/**
 * Builds a report detail DTO from a flat performance item and local category data.
 * Reconstructs the merged BranchAssignment DTO shape for downstream report-screen
 * consumers that still read nested paths.
 */
export const buildReportDetailDTOFromPerformance = (
  performanceItem: IPerformanceItem,
  categoryDTOList: ICategoryRef[],
  options: IOutcomeTreeBuildOptions = {},
) => {
  const { activityScoreDetailDTOList, learnerScore } = performanceItem;

  const categoryActivityAnswerDTOList = buildCategoryActivityAnswerDTOList(
    stampScoreDetailsWithEventAt(performanceItem),
    categoryDTOList,
    options,
  );

  const activityTemplateScoreDTOList = (activityScoreDetailDTOList || []).map(
    (detail) => ({
      activityId: detail.activityId,
      activityTemplateId: detail.activityTemplateId,
      activityTemplateType: detail.activityTemplateType,
      isEmpty: detail.empty,
      score: detail.score,
    }),
  );

  const branchAssignmentDTO = {
    id: performanceItem.branchAssignmentId,
    name: performanceItem.name,
    description: performanceItem.description,
    coterieType: performanceItem.coterieType,
    curriculumType: performanceItem.curriculumType,
    createdAt: performanceItem.createdAt,
    beginAt: performanceItem.beginAt,
    endAt: performanceItem.endAt,
    isEvaluated: performanceItem.isEvaluated,
  };

  return {
    id: performanceItem.branchAssignmentId,
    score: learnerScore,
    branchAssignmentDTO,
    branchAssignmentUserDTO: {
      activityTemplateScoreDTOList,
    },
    categoryActivityAnswerDTOList,
  };
};
